import type { ComponentType } from 'react';

export interface DeferredSkeleton {
  pattern: RegExp;
  Component: ComponentType | null;
  load: () => Promise<unknown>;
}

const deferred = (pattern: RegExp, loader: () => Promise<ComponentType>): DeferredSkeleton => {
  let pending: Promise<unknown> | null = null;
  const entry: DeferredSkeleton = {
    pattern,
    Component: null,
    load: () =>
      (pending ??= loader().then(
        (Component) => {
          entry.Component = Component;
        },
        () => {
          pending = null;
        }
      ))
  };
  return entry;
};

const DEFERRED_SKELETONS: DeferredSkeleton[] = [
  deferred(/^\/agenda\/?$/, () =>
    import('@/containers/Agenda/components/AgendaSkeleton/AgendaRouteSkeleton').then(
      (m) => m.AgendaRouteSkeleton
    )
  ),
  deferred(/^\/mi-pagina\/?$/, () =>
    import('@/containers/StoreFrontBuilder/BuilderFallback').then((m) => m.BuilderFallback)
  ),
  deferred(/^\/actividad(?:\/[a-z]+)?\/?$/, () =>
    import('@/containers/Activities/RouteSkeleton').then((m) => m.ActivitiesRouteSkeleton)
  ),
  deferred(/^\/equipo\/?$/, () => import('@/containers/Team/Skeleton').then((m) => m.Skeleton)),
  deferred(/^\/facturacion\/?$/, () =>
    import('@/containers/Billing/components/BillingSkeleton').then((m) => m.BillingSkeleton)
  ),
  // Inicio y Clientes necesitan la sesión (saludo, plan, negocio): sin ella dibujan el genérico.
  deferred(/^\/$/, () =>
    import('@/containers/Dashboard/RouteSkeleton').then((m) => m.DashboardRouteSkeleton)
  ),
  deferred(/^\/clientes\/?$/, () =>
    import('@/containers/Clients/RouteSkeleton').then((m) => m.ClientsRouteSkeleton)
  ),
  // Contenido IG: the hub, the builder (or its desktop handoff) and the guide, each with its own shape.
  deferred(/^\/contenido-ig\/?$/, () =>
    import('@/containers/Marketing/ContenidoIG/RouteSkeleton').then((m) => m.HubRouteSkeleton)
  ),
  deferred(/^\/contenido-ig\/posteo\/?$/, () =>
    import('@/containers/Marketing/ContenidoIG/RouteSkeleton').then((m) => m.BuilderRouteSkeleton)
  ),
  deferred(/^\/contenido-ig\/guia\/?$/, () =>
    import('@/containers/IgScreenshots/Skeleton').then((m) => m.GuideRouteSkeleton)
  ),
  // Novedades: el mismo encabezado, la columna y las filas que pinta la pantalla al esperar el primer sync.
  deferred(/^\/notificaciones\/?$/, () =>
    import('@/containers/Notifications/Skeleton').then((m) => m.NotificationsRouteSkeleton)
  ),
  // Catálogo: cada índice dibuja en la ruta el mismo esqueleto que en su primera carga.
  deferred(/^\/productos\/?$/, () =>
    import('@/containers/Products/RouteSkeleton').then((m) => m.ProductsRouteSkeleton)
  ),
  deferred(/^\/eventos\/?$/, () =>
    import('@/containers/UniqueEvents/RouteSkeleton').then((m) => m.UniqueEventsRouteSkeleton)
  ),
  deferred(/^\/cursos-online\/?$/, () =>
    import('@/containers/OnlineCourses/RouteSkeleton').then((m) => m.OnlineCoursesRouteSkeleton)
  ),
  deferred(/^\/planesmensuales\/?$/, () =>
    import('@/containers/Subscriptions/RouteSkeleton').then((m) => m.SubscriptionsRouteSkeleton)
  ),
  deferred(/^\/descargables\/?$/, () =>
    import('@/containers/Downloadables/RouteSkeleton').then((m) => m.DownloadablesRouteSkeleton)
  ),
  deferred(/^\/tarjetas-de-regalo\/?$/, () =>
    import('@/containers/GiftCards/RouteSkeleton').then((m) => m.GiftCardsRouteSkeleton)
  ),
  deferred(/^\/servicios\/?$/, () =>
    import('@/containers/Services/RouteSkeleton').then((m) => m.ServicesRouteSkeleton)
  ),
  // Referidos, Buzón de ideas y Comunidad: la misma cabecera y el mismo cuerpo que la página.
  deferred(/^\/referidos\/?$/, () =>
    import('@/containers/Referrals/RouteSkeleton').then((m) => m.ReferralsRouteSkeleton)
  ),
  deferred(/^\/ideas\/?$/, () =>
    import('@/containers/Ideas/RouteSkeleton').then((m) => m.IdeasRouteSkeleton)
  ),
  deferred(/^\/comunidad\/?$/, () =>
    import('@/containers/Community/RouteSkeleton').then((m) => m.CommunityRouteSkeleton)
  ),
  // Finanzas › Resumen: hero, pestañas del celular, filtros y el cockpit con lo de la última visita.
  deferred(/^\/finanzas\/?$/, () =>
    import('@/containers/Finanzas/RouteSkeleton').then((m) => m.FinanzasRouteSkeleton)
  ),
  // Área B: Promotores (la página con la tabla en carga).
  deferred(/^\/promotores\/?$/, () =>
    import('@/containers/Promoters/components/PromotersSkeleton').then((m) => m.PromotersRouteSkeleton)
  ),
  // Área B: la ficha del cliente (hero + Skeleton de Client, con el aviso si la ficha lo tenía).
  deferred(/^\/clientes\/\d+\/?$/, () =>
    import('@/containers/Client/components/RouteSkeleton').then((m) => m.ClientRouteSkeleton)
  ),
  // Área B: Asistencia y su historial (con la forma de la última visita).
  deferred(/^\/equipo\/asistencia\/?$/, () =>
    import('@/containers/Attendance/components/AttendanceSkeleton').then((m) => m.AttendanceRouteSkeleton)
  ),
  deferred(/^\/equipo\/asistencia\/historial\/?$/, () =>
    import('@/containers/Attendance/components/AttendanceSkeleton').then(
      (m) => m.AttendanceHistoryRouteSkeleton
    )
  )
];

export const findDeferred = (pathname: string): DeferredSkeleton | undefined =>
  DEFERRED_SKELETONS.find(({ pattern }) => pattern.test(pathname));

export const preloadRouteSkeleton = (pathname: string): Promise<unknown> | undefined =>
  findDeferred(pathname)?.load();

// Only the entry URL is critical. Other skeletons warm on explicit navigation intent.
if (typeof window !== 'undefined') void preloadRouteSkeleton(window.location.pathname);
